import { router } from "@inertiajs/react"
import { Clock, Mail } from "lucide-react"

import { Avatar } from "@/components/tuw/Avatar"
import StatusBadge from "@/components/tuw/StatusBadge"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/orderActions"

import { AddProjectChip, GrantChip } from "./GrantChip"
import PersonMenu from "./PersonMenu"

// One person I've shared with, grouped across all the orders they can view. All
// writes go through Inertia's router with the Rails CSRF header (never /api/v1 —
// F1), and each redirects to /access so the page re-renders from the server (no
// optimistic client state to keep in sync — D-D). `onAddProject` opens the invite
// modal locked to this person's email.
export default function PersonCard({ person, onAddProject }) {
  const headers = { "X-CSRF-Token": csrfToken() }

  const removeGrant = (grant) =>
    router.delete(`/access/grants/${grant.accessId}`, { headers, preserveScroll: true })

  // "Remove all access" dispatches by kind: active removes every row for the
  // manager_id (PR-001 — the explicit contract field, never parsed from `key`);
  // pending cancels the invite by email (no manager id exists yet).
  const removeAll = () => {
    if (person.kind === "active") {
      router.delete(`/access/people/${person.managerId}`, { headers, preserveScroll: true })
    } else {
      router.delete("/access/pending", { headers, data: { email: person.email }, preserveScroll: true })
    }
  }

  const resendInvite = () =>
    router.post("/access/resend", { email: person.email }, { headers, preserveScroll: true })

  const cancelInvite = () =>
    router.delete("/access/pending", { headers, data: { email: person.email }, preserveScroll: true })

  const grantCount = person.grants.length

  return (
    <div className="mb-3 rounded-[14px] border border-border bg-card px-[18px] py-4">
      <div className="flex items-center gap-[13px]">
        <Avatar initials={person.initials} name={person.name} size={42} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-[9px]">
            <span className="truncate font-display text-[14.5px] font-bold tracking-[-0.2px] text-foreground">
              {person.name}
            </span>
            <StatusBadge tier={person.status.tier} label={person.status.label} />
          </div>
          <div className="mt-0.5 truncate text-[12px] text-ink-3">{person.email}</div>
        </div>
        <PersonMenu onAddProject={() => onAddProject(person)} onRemoveAll={removeAll} />
      </div>

      <div className="mt-[13px] border-t border-border pt-[13px]">
        <div className="mb-[9px] text-[10.5px] font-bold tracking-[0.08em] text-ink-3 uppercase">
          Can view {grantCount} {grantCount === 1 ? "project" : "projects"}
        </div>
        <div className="flex flex-wrap gap-2">
          {person.grants.map((grant) => (
            <GrantChip key={grant.accessId} grant={grant} onRemove={() => removeGrant(grant)} />
          ))}
          <AddProjectChip onClick={() => onAddProject(person)} />
        </div>
      </div>

      {person.kind === "pending" && (
        <div className="mt-3 flex items-center gap-2 border-t border-dashed border-line-2 pt-3 text-[11.5px] text-ink-3">
          <Clock className="size-[14px] shrink-0 text-ink-3" strokeWidth={1.9} />
          <span>
            Invited {person.invitedLabel} · awaiting sign-up
          </span>
          <span className="ml-auto flex gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={resendInvite}>
              <Mail className="size-[14px]" strokeWidth={1.9} />
              Resend
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={cancelInvite}>
              Cancel invite
            </Button>
          </span>
        </div>
      )}
    </div>
  )
}
